import React, { FunctionComponent, useEffect } from 'react';
import { graphql } from 'react-relay';
import { usePaginationFragment } from 'react-relay/hooks';
import { CatalogListContent } from 'src/components/widgets/catalogList';
import { CatalogListItemModel } from 'src/components/widgets/catalogList/catalogListItemModel';
import { Badge } from 'src/components/widgets/badge';
import { Checkbox } from 'src/components/widgets/checkbox';
import { Row, Table } from 'src/components/widgets/table';
import { T } from 'src/shared/helpers';
import { useInfiniteScroll } from 'src/shared/hooks';
import NotFoundIcon from 'src/shared/material_icons/image/broken_image.svg';
import { RelayFragment } from 'src/shared/types';
import styled from 'styled-components';
import { Center } from 'src/shared/globals';
import { EmptyList } from 'src/components/widgets/emptyList';
import { merchantProduct_item$data } from '../state/queries/__generated__/merchantProduct_item.graphql';
import { storeCatalogPaginationFragmentQuery } from './__generated__/storeCatalogPaginationFragmentQuery.graphql';
import { storeCatalogPaginationFragment__merchantProducts$key } from './__generated__/storeCatalogPaginationFragment__merchantProducts.graphql';
import { storeCatalogQuery$data } from './__generated__/storeCatalogQuery.graphql';

interface StoreCatalogPaginationFragmentProps {
    query: storeCatalogQuery$data;
    search: string;
    scrollContainer?: HTMLDivElement | null;
    selectedProductsSKUs?: string[];
    showSelectionCheckboxes?: boolean;
    onProductClick: (merchantProduct: Omit<RelayFragment<merchantProduct_item$data>, ' $fragmentType'>, hideAlreadyConfiguredBadge?: boolean) => void;
    hideAlreadyConfiguredBadge?: boolean;
    hideProductsNotConfigured?: boolean;
    hideAlreadyConfiguredProducts?: boolean;
    disableProductCode?: string;
    columns?: { xs: number; md: number; lg: number };
    viewMode?: 'grid' | 'list';
}

const ProductThumbnail = styled.img`
    width: 36px;
    height: 36px;
    object-fit: contain;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
`;

const ThumbnailPlaceholder = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 4px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
`;

const AlreadyConfiguredBadge = styled(Badge).attrs({ variant: 'info' as const })`
    background-color: #d5f38b;
`;

const ProductNameCell = styled.span`
    display: flex;
    align-items: center;
    gap: 10px;
`;

// List component
const StoreCatalogPaginationFragment: FunctionComponent<StoreCatalogPaginationFragmentProps> = (props) => {
    const { data, loadNext, hasNext, refetch, isLoadingNext } = usePaginationFragment<
        storeCatalogPaginationFragmentQuery,
        storeCatalogPaginationFragment__merchantProducts$key
    >(
        graphql`
            fragment storeCatalogPaginationFragment__merchantProducts on Query
            @refetchable(queryName: "storeCatalogPaginationFragmentQuery")
            @argumentDefinitions(
                count: { type: "Int", defaultValue: 20 }
                cursor: { type: "String", defaultValue: null }
                search: { type: "String", defaultValue: "" }
                salesChannelID: { type: "ID!" }
            ) {
                merchantProducts(first: $count, after: $cursor, search: $search, salesChannelID: $salesChannelID)
                    @connection(key: "storeCatalogPaginationFragment_merchantProducts") {
                    edges {
                        node {
                            ...merchantProduct_item @relay(mask: false)
                        }
                    }
                }
            }
        `,
        props.query
    );

    useEffect(() => {
        refetch({ count: 20, search: props.search }, { fetchPolicy: 'store-and-network' });
    }, [props.search]);

    const handleProductClick = (model: CatalogListItemModel, hideAlreadyConfiguredBadge: boolean | undefined) => {
        const merchantProduct = data.merchantProducts?.edges?.find((x) => x.node?.sku == model.subtitle);
        if (merchantProduct && merchantProduct.node) {
            props.onProductClick(merchantProduct.node, hideAlreadyConfiguredBadge);
        }
    };

    const handleRowClick = (edge: (typeof products)[number]) => {
        if (edge.node) {
            props.onProductClick(edge.node, props.hideAlreadyConfiguredBadge);
        }
    };

    let products = data.merchantProducts?.edges ?? [];
    if (props.hideProductsNotConfigured) {
        products = products.filter((x) => x.node?.isConfiguratedInZakeke);
    }
    if (props.hideAlreadyConfiguredProducts) {
        products = products.filter((x) => !x.node?.isConfiguratedInZakeke);
    }

    useInfiniteScroll(() => loadNext(20), hasNext, props.scrollContainer, isLoadingNext);

    const noItemsSubMessage =
        props.search !== ''
            ? T._('Sorry, no product was found for your search. Please try using different keywords.', 'Admin')
            : T._('Currently, there are no products available in this store. Please create at least one product in your store.', 'Admin');

    if (props.viewMode === 'list') {
        return (
            <div>
                {products.length === 0 && !isLoadingNext && (
                    <Center>
                        <EmptyList icon={<NotFoundIcon />} title={T._('No products found', 'Admin')} subTitle={noItemsSubMessage} />
                    </Center>
                )}
                {products.length > 0 && (
                    <Table
                        headings={[
                            ...(props.showSelectionCheckboxes ? [['', '44px'] as [string, string]] : []),
                            [T._('Name', 'Admin'), '1fr'],
                            [T._('Code', 'Admin'), '250px'],
                            ...(props.hideAlreadyConfiguredBadge ? [] : [[T._('Status', 'Admin'), '200px'] as [string, string]])
                        ]}
                        compact
                    >
                        {products.map((x) => {
                            const isSelected = !!x.node.sku && props.selectedProductsSKUs?.includes(x.node.sku) === true;
                            const isNotSelectable = x.node.isConfiguratedInZakeke && !props.hideAlreadyConfiguredBadge;

                            return (
                                <Row
                                    key={x.node.sku}
                                    selected={isSelected}
                                    disabled={props.disableProductCode === x.node.sku}
                                    onClick={isNotSelectable ? undefined : () => handleRowClick(x)}
                                    cells={[
                                        ...(props.showSelectionCheckboxes
                                            ? [
                                                  <Checkbox
                                                      key='selection'
                                                      checked={isSelected}
                                                      disabled={isNotSelectable}
                                                      onClick={(event) => event.stopPropagation()}
                                                      onChange={() => handleRowClick(x)}
                                                      variant='compact'
                                                  />
                                              ]
                                            : []),
                                        <ProductNameCell key='name'>
                                            {x.node.thumbnail ? <ProductThumbnail src={x.node.thumbnail} alt={x.node.name} /> : <ThumbnailPlaceholder />}
                                            {x.node.name}
                                        </ProductNameCell>,
                                        x.node.sku,
                                        ...(props.hideAlreadyConfiguredBadge
                                            ? []
                                            : [
                                                  x.node.isConfiguratedInZakeke ? (
                                                      <AlreadyConfiguredBadge key='status'>{T._('Already Configured', 'Admin')}</AlreadyConfiguredBadge>
                                                  ) : null
                                              ])
                                    ]}
                                />
                            );
                        })}
                    </Table>
                )}
            </div>
        );
    }

    return (
        <CatalogListContent
            scrollContainer={props.scrollContainer}
            models={products?.map((x) => ({
                thumbnail: x.node.thumbnail,
                title: x.node.name,
                subtitle: x.node.sku,
                isConfiguratedInZakeke: x.node.isConfiguratedInZakeke,
                id: x.node.sku,
                selected: x.node.sku ? props.selectedProductsSKUs?.indexOf(x.node.sku) !== -1 : false
            }))}
            columns={props.columns ?? { xs: 1, md: 5, lg: 8 }}
            loadNext={() => loadNext(20)}
            hasNext={hasNext}
            isLoadingNext={isLoadingNext}
            onItemClick={handleProductClick}
            noItemsIcon={<NotFoundIcon />}
            noItemsMessage={T._('No products found', 'Admin')}
            noItemsSubMessage={noItemsSubMessage}
            hideAlreadyConfiguredBadge={props.hideAlreadyConfiguredBadge}
            disableProductCode={props.disableProductCode}
            fillHeight
        />
    );
};

export default StoreCatalogPaginationFragment;
